import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import Layout from './components/Layout';
import { Loader } from 'lucide-react';

const AboutUs = lazy(() => import('./pages/AboutUs'));
const LearnMore = lazy(() => import('./pages/LearnMore'));
const Login = lazy(() => import('./pages/Login'));
const Survey = lazy(() => import('./pages/Survey'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Store = lazy(() => import('./pages/store/Store'));
const Success = lazy(() => import('./pages/store/Success'));
const Workouts = lazy(() => import('./pages/workouts/Workouts'));
const WorkoutDetail = lazy(() => import('./pages/workouts/WorkoutDetail'));
const Goals = lazy(() => import('./pages/Goals'));
const Progress = lazy(() => import('./pages/Progress'));
const Community = lazy(() => import('./pages/community/Community'));
const Chat = lazy(() => import('./pages/Chat'));
const Glossary = lazy(() => import('./pages/Glossary'));
const Settings = lazy(() => import('./pages/Settings'));
const Contact = lazy(() => import('./pages/Contact'));
const AdminSuite = lazy(() => import('./pages/admin/AdminSuite'));
const NotFound = lazy(() => import('./pages/NotFound'));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader className="w-10 h-10 text-cyan-500 animate-spin" />
    </div>
  );
}

function AppLoader() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const user = useAuthStore(state => state.user);
  const loading = useAuthStore(state => state.loading);

  if (loading) return <AppLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.surveyCompleted && !user.isAdmin) return <Navigate to="/survey" replace />;
  
  return children;
}

function AdminRoute({ children }) {
  const user = useAuthStore(state => state.user);
  const loading = useAuthStore(state => state.loading);

  if (loading) return <AppLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.isAdmin) return <Navigate to="/dashboard" replace />;
  
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public - no auth required */}
          <Route path="/" element={<AboutUs />} />
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/learn-more" element={<LearnMore />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/survey" element={<Survey />} />
          <Route path="/store/success" element={<Success />} />

          {/* Protected */}
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/store" element={<Store />} />
            <Route path="/workouts" element={<Workouts />} />
            <Route path="/workouts/:id" element={<WorkoutDetail />} />
            <Route path="/goals" element={<Goals />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/community" element={<Community />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/glossary" element={<Glossary />} />
            <Route path="/settings" element={<Settings />} />
          </Route>

          {/* Admin */}
          <Route path="/admin-suite" element={<AdminRoute><Layout /></AdminRoute>}>
            <Route index element={<AdminSuite />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
